import { use } from 'react';
import useSWR, { mutate } from 'swr';
import { TenantSelectionContext } from 'contexts/TenantSelectionContext';
import useAuth from 'hooks/useAuth';
import axios from 'utils/axios';

// Notifications belong to a tenant. Each request names its tenant explicitly, because the default
// X-Tenant-Id comes from storage shared by every browser tab and can differ from this tab's context.
const tenantHeaders = (tenantId: number) => ({ 'X-Tenant-Id': String(tenantId) });

export interface ReportNotification {
  notificationId: string;
  tenantId: number;
  title: string;
  message: string;
  type?: string;
  relatedExportId?: string;
  relatedScheduleId?: string;
  createdAt: string;
  readAt?: string;
}
export function useNotificationCount() {
  const { user } = useAuth();
  const tenantId = use(TenantSelectionContext)?.tenantId ?? null;
  return useSWR<number>(
    user?.id && tenantId ? ['notifications', user.id, tenantId, 'count'] : null,
    async () => (await axios.get<number>('api/notifications/unread-count', { headers: tenantHeaders(tenantId!) })).data,
    { refreshInterval: 30_000, revalidateOnFocus: true, revalidateOnReconnect: true }
  );
}
export function useNotifications(
  query: { fromUtc?: string; toUtcExclusive?: string; readState: string; page: number; pageSize: number },
  enabled = true
) {
  const { user } = useAuth();
  const tenantId = use(TenantSelectionContext)?.tenantId ?? null;
  return useSWR<{ items: ReportNotification[]; totalCount: number }>(
    enabled && user?.id && tenantId ? ['notifications', user.id, tenantId, query] : null,
    async () => (await axios.get('api/notifications', { params: query, headers: tenantHeaders(tenantId!) })).data,
    { refreshInterval: 30_000, revalidateOnFocus: true, revalidateOnReconnect: true }
  );
}
export async function setNotificationRead(id: string, read: boolean, tenantId: number) {
  await axios.put(`api/notifications/${id}/read`, read, { headers: { 'Content-Type': 'application/json', ...tenantHeaders(tenantId) } });
  await refreshNotifications();
}
export async function dismissNotification(id: string, tenantId: number) {
  await axios.post(`api/notifications/${id}/dismiss`, undefined, { headers: tenantHeaders(tenantId) });
  await refreshNotifications();
}
export async function refreshNotifications() {
  await mutate((key: unknown) => Array.isArray(key) && key[0] === 'notifications');
}
